// Phase 3 candidate video — chunked upload DURING the interview (Deploy F).
//
// The recorder emits a chunk every 10 s; each one is uploaded straight away (one at a time, in order,
// with retries) as an Azure block. At the end only the last chunk + a small "commit" call remain, so
// the candidate no longer waits for a 280 MB upload, and closing the tab loses at most the last
// seconds. Chunks stay in memory (as before) so a missing one can be re-sent, and so the whole-file
// upload can still be used if the chunked path is unavailable (older backend) or keeps failing.
import { uploadWithRetry } from './videoUpload';

export const CHUNK_RETRY_DELAYS_MS = [1000, 3000, 8000];

export function makeRecordingId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `rec-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * sendChunk(index, blob) and sendCommit(count) resolve { ok, status, error, missing? } and never reject.
 * Returns { add(blob), finish(onProgress), state() }.
 */
export function createChunkUploader({ sendChunk, sendCommit, delays = CHUNK_RETRY_DELAYS_MS,
                                      sleep = (ms) => new Promise((r) => setTimeout(r, ms)) }) {
  const chunks = [];
  const failed = new Set();
  let sent = 0;
  let unavailable = false;            // backend without the chunk endpoint / rejected credentials
  let chain = Promise.resolve();

  const sendOne = async (index) => {
    if (unavailable) { failed.add(index); return; }
    const result = await uploadWithRetry(() => sendChunk(index, chunks[index]), { retries: delays.length, delays, sleep });
    if (result.ok) {
      failed.delete(index);
      sent += 1;
    } else {
      failed.add(index);
      const status = result.last && result.last.status;
      if (status === 404 || status === 401 || status === 405) unavailable = true;
    }
  };

  return {
    add(blob) {
      const index = chunks.push(blob) - 1;
      chain = chain.then(() => sendOne(index));
      return index;
    },
    state() {
      return { chunks: chunks.length, sent, failed: [...failed], unavailable };
    },
    async finish(onProgress) {
      await chain;                                         // every chunk tried at least once (in order)
      if (unavailable || chunks.length === 0) return { ok: false, reason: unavailable ? 'unavailable' : 'empty' };
      const report = () => onProgress && onProgress(Math.round((sent / chunks.length) * 100));
      report();
      for (const index of [...failed]) { await sendOne(index); report(); }      // one more pass for failures
      for (let round = 0; round < 2; round += 1) {
        const commit = await sendCommit(chunks.length);
        if (commit.ok) return { ok: true, chunks: chunks.length };
        if (commit.status !== 409 || !Array.isArray(commit.missing)) return { ok: false, reason: commit.error || 'commit failed' };
        for (const index of commit.missing) { await sendOne(index); report(); }   // server says these never arrived
      }
      return { ok: false, reason: 'chunks still missing after re-sending' };
    },
  };
}
